import { useCallback, useState } from "react";
import ApiClientAsync from "../api/ApiClientAsync";
import { gusResponseToInvoiceFormData } from "../redux/modules/invoices/dto/gusResponseToCompanyInvoiceFormData";
import { getCompanyDataSearchErrorMessage } from "../redux/modules/invoices/dto/companyDataSearchErrorMessage";
import {
  BUSINESS_ACTIVITY_KIND,
  FORM_DATA_KEY_BY_KIND
} from "../redux/modules/invoices/invoiceFields";

const DEFAULT_FORM_DATA_KEY =
  FORM_DATA_KEY_BY_KIND[BUSINESS_ACTIVITY_KIND.OTHER_BUSINESS];

/**
 * Shared hook for fetching company data from GUS (Polish company registry) by NIP.
 * Used by other-business and self-employed invoice forms (web + native).
 *
 * @param {Object} params
 * @param {string} params.locale
 * @param {string} params.nip
 * @param {string} [params.formDataKey] - invoice form slice to write GUS result into
 *   (e.g. "otherBusinessData" | "selfEmployedData"); defaults to "otherBusinessData".
 * @param {Function} params.updateInvoiceFormData
 * @param {Function} params.updateInvoiceFormError
 * @param {Object|null} params.invoiceFormErrors
 * @param {Function} params.translate
 * @returns {{ fetchGus: Function, loadingGus: boolean }}
 */
const useFetchGus = ({
  locale,
  nip,
  formDataKey = DEFAULT_FORM_DATA_KEY,
  updateInvoiceFormData,
  updateInvoiceFormError,
  invoiceFormErrors,
  translate
}) => {
  const [loadingGus, setLoadingGus] = useState(false);

  const fetchGus = useCallback(async () => {
    try {
      setLoadingGus(true);
      const response = await new ApiClientAsync(locale).get({
        urlPath: "app.invoices.companyData",
        variables: { nip }
      });

      const formDataFromGus = gusResponseToInvoiceFormData(response);

      updateInvoiceFormData({
        [formDataKey]: formDataFromGus
      });
      updateInvoiceFormError({
        [formDataKey]: null
      });
    } catch (error) {
      updateInvoiceFormError({
        [formDataKey]: {
          ...(invoiceFormErrors?.[formDataKey] ?? {}),
          nip: getCompanyDataSearchErrorMessage(
            error,
            translate("invoice-form_nip-not-valid")
          )
        }
      });
    } finally {
      setLoadingGus(false);
    }
  }, [
    locale,
    nip,
    formDataKey,
    updateInvoiceFormData,
    updateInvoiceFormError,
    invoiceFormErrors,
    translate
  ]);

  return { fetchGus, loadingGus };
};

export default useFetchGus;
